import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { isPlatformServer } from '@angular/common';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

import { LanguageService } from '@fcom/ui-translate';
import { IconPosition } from '@fcom/ui-components';
import { serializeParams, unsubscribe } from '@fcom/core/utils';
import { DateFormatPipe } from '@fcom/common';
import { QualtricsSurveys } from '@fcom/common/qualtrics/qualtrics.utils';
import { RootPaths } from '@fcom/core';
import { FlightV2 } from '@fcom/one-upgrade/api/models/flight-v-2';
import { Cabin } from '@fcom/one-upgrade/api/models/cabin';
import { UpgradeProduct } from '@fcom/one-upgrade/api/models/upgrade-product';

import { BookingRef, ExtendedFlightBasket, PaymentType } from '../../interfaces';
import { DataState, State } from '../../interfaces/store.interface';
import { DataActions, StatusActions } from '../../store/actions';
import {
  getBasketFlights,
  getBasketPassengerIds,
  getBookingRef,
  getSplitPNR,
  getWaitlisted,
  selectDataState,
} from '../../store';
import { UpgradeAnalyticsService } from '../../services/upgrade-analytics.service';
import { basketHasWaitlistUpgrade } from '../../utils/basket.utils';

@Component({
  selector: 'fin-upgrade-success',
  templateUrl: 'success.component.html',
  providers: [DateFormatPipe],
})
export class SuccessComponent implements OnInit, OnDestroy {
  readonly IconPosition = IconPosition;
  readonly survey = QualtricsSurveys.UPGRADE_FEEDBACK;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  storeData$: Observable<DataState>;
  basketFlights$: Observable<
    { flight: FlightV2; flightId: string; cabin: Cabin; productName: UpgradeProduct; paymentType: PaymentType }[]
  >;
  basketPassengers$: Observable<string[]>;
  bookingRef$: Observable<BookingRef>;
  waitlisted$: Observable<boolean>;
  splitPNR$: Observable<boolean>;
  surveyOpen = false;
  subscriptions: Subscription = new Subscription();

  constructor(
    private store$: Store<State>,
    private languageService: LanguageService,
    private router: Router,
    private analyticsService: UpgradeAnalyticsService,
    @Inject(PLATFORM_ID) private platform: object
  ) {}

  ngOnInit(): void {
    this.storeData$ = this.store$.pipe(select(selectDataState));
    this.basketFlights$ = this.store$.pipe(select(getBasketFlights));
    this.basketPassengers$ = this.store$.pipe(select(getBasketPassengerIds));
    this.bookingRef$ = this.store$.pipe(select(getBookingRef));
    this.waitlisted$ = this.store$.pipe(select(getWaitlisted));
    this.splitPNR$ = this.store$.pipe(select(getSplitPNR));

    if (isPlatformServer(this.platform)) {
      // Skip data processing on server-side
      return;
    }

    this.subscriptions.add(
      this.setNotificationsAndGAEvent().subscribe(() => {
        this.store$.dispatch(DataActions.clearAll());
      })
    );
  }

  setNotificationsAndGAEvent(): Observable<[ExtendedFlightBasket[], string[], DataState]> {
    return combineLatest([this.basketFlights$, this.basketPassengers$, this.storeData$]).pipe(
      take(1),
      tap(([flightBasket, passengers, data]) => {
        // Page has been reloaded and we don't need to send another tracking event.
        if (!flightBasket.length) {
          return;
        }
        this.store$.dispatch(
          StatusActions.setWaitlisted({
            waitlisted: basketHasWaitlistUpgrade(flightBasket),
          })
        );

        this.store$.dispatch(
          StatusActions.setSplitPNR({
            splitPNR: passengers.length < data.passengers.length,
          })
        );

        this.analyticsService.sendPurchaseEvent();

        // clear basket and data
        this.store$.dispatch(DataActions.clearAll());
      })
    );
  }

  getFragment(): Observable<string> {
    return this.waitlisted$.pipe(
      take(1),
      map((x) => (x ? 'fragments.oneUpgrade.successWaitlisted.url' : 'fragments.oneUpgrade.success.url'))
    );
  }

  redirectToMMB(): void {
    this.subscriptions.add(
      combineLatest([this.bookingRef$, this.splitPNR$])
        .pipe(take(1))
        .subscribe(([bookingRef, splitPNR]) => {
          const params = splitPNR ? '' : `?${serializeParams({ ...bookingRef })}`;

          this.router.navigateByUrl(
            `/${this.languageService.langValue || 'en'}/${RootPaths.MANAGE_BOOKING_ROOT}${params}`
          );
        })
    );
  }

  openSurvey(): void {
    this.surveyOpen = true;
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }
}
